import {
  type ContactList,
  type DirectorySource,
  directorySourceSchema,
  type FileSource,
  LIST_COLORS,
  type ListColor,
} from "@kontaktfix/shared";
import {
  Button,
  Card,
  cn,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from "@kontaktfix/ui";
import { ListChecksIcon, PlusIcon } from "@phosphor-icons/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DirectorySourceFields } from "@/components/directory-source";
import { FileSourceFields } from "@/components/file-source";
import { LIST_COLOR_HEX } from "@/components/list-badge";
import { Page, PageHeader } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatRelative } from "@/lib/i18n";
import { useInvalidateData, useLists, useMe, useMembers } from "@/lib/queries";

const emptyFile: FileSource = {
  url: "",
  authType: "none",
  username: "",
  schedule: "daily",
  mapping: {},
  keyColumn: null,
  deletion: "after_days",
  deletionDays: 7,
};

/** Opens the dialog with a kind chosen elsewhere, e.g. from the integrations settings. */
export function ListFormDialog({
  open,
  onOpenChange,
  list,
  initialKind = "manual",
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list?: ContactList;
  initialKind?: ContactList["kind"];
  onSaved?: (list: ContactList) => void;
}) {
  const { t } = useTranslation();
  const invalidate = useInvalidateData();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<ListColor>("iris");
  const [kind, setKind] = useState<ContactList["kind"]>("manual");
  const [directory, setDirectory] = useState<DirectorySource>(() =>
    directorySourceSchema.parse({ scope: "all" }),
  );
  const [file, setFile] = useState<FileSource>(emptyFile);
  const [fileSecret, setFileSecret] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(list?.name ?? "");
    setDescription(list?.description ?? "");
    setColor(list?.color ?? "iris");
    setKind(list?.kind ?? initialKind);
    setDirectory(directorySourceSchema.parse(list?.directory ?? { scope: "all" }));
    if (list?.file) {
      const { hasSecret: _h, refreshedAt: _r, error: _e, stats: _s, ...source } = list.file;
      setFile(source);
    } else setFile(emptyFile);
    setFileSecret("");
  }, [open, list, initialKind]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        name,
        description: description || null,
        color,
        kind,
        directory: kind === "directory" ? directory : null,
        file: kind === "file" ? file : null,
        fileSecret: kind === "file" && fileSecret ? fileSecret : undefined,
      };
      const saved = list
        ? await api.patch<ContactList>(`/api/lists/${list.id}`, body)
        : await api.post<ContactList>("/api/lists", body);
      toast.success(t(list ? "lists.updated" : "lists.created"));
      await invalidate();
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={list ? t("common.edit") : t("lists.new")}>
      <form onSubmit={save} className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto pb-5">
        {!list && (
          <Field label={t("lists.form.kind")} htmlFor="list-kind" hint={t(`lists.form.kindHint.${kind}`)}>
            <Select
              id="list-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as ContactList["kind"])}
            >
              <option value="manual">{t("lists.form.kinds.manual")}</option>
              <option value="directory">{t("lists.form.kinds.directory")}</option>
              <option value="file">{t("lists.form.kinds.file")}</option>
            </Select>
          </Field>
        )}
        <Field label={t("lists.form.name")} htmlFor="list-name">
          <Input
            id="list-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("lists.form.namePlaceholder")}
            required
            autoFocus
          />
        </Field>
        <Field label={t("lists.form.description")} htmlFor="list-desc">
          <Textarea
            id="list-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </Field>
        <Field label={t("lists.form.color")}>
          <div className="flex flex-wrap gap-2">
            {LIST_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={c}
                className={cn(
                  "size-6 rounded-full border-2 transition-transform",
                  color === c ? "scale-110 border-fg" : "border-transparent",
                )}
                style={{ background: LIST_COLOR_HEX[c] }}
              />
            ))}
          </div>
        </Field>
        {kind === "directory" && <DirectorySourceFields value={directory} onChange={setDirectory} />}
        {kind === "file" && (
          <FileSourceFields
            value={file}
            onChange={setFile}
            secret={fileSecret}
            onSecretChange={setFileSecret}
            hasSecret={list?.file?.hasSecret ?? false}
            listId={list?.id}
          />
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={!name.trim() || (kind === "file" && !Object.values(file.mapping).some(Boolean))}
          >
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function ListsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/app/lists" });
  const { data: me } = useMe();
  const { data: lists, isLoading } = useLists();
  const { data: members = [] } = useMembers();
  const [creating, setCreating] = useState(Boolean(search.new));
  const isAdmin = me?.role === "admin";
  const memberName = (id: string) => members.find((m) => m.id === id)?.name;

  return (
    <Page>
      <PageHeader
        title={t("lists.title")}
        description={t("lists.description")}
        actions={
          isAdmin && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <PlusIcon />
              {t("lists.new")}
            </Button>
          )
        }
      />
      <Card>
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : !lists?.length ? (
          <EmptyState
            icon={<ListChecksIcon />}
            title={t("lists.empty")}
            description={t("lists.emptyText")}
            action={
              isAdmin && (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  <PlusIcon />
                  {t("lists.new")}
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t("lists.columns.name")}</Th>
                <Th className="text-right">{t("lists.columns.contacts")}</Th>
                <Th className="text-right">{t("lists.columns.recipients")}</Th>
                <Th className="hidden md:table-cell">{t("lists.columns.editors")}</Th>
                <Th className="hidden text-right sm:table-cell">{t("lists.columns.updated")}</Th>
              </tr>
            </thead>
            <tbody>
              {lists.map((list) => (
                <Tr
                  key={list.id}
                  onClick={() => navigate({ to: "/lists/$listId", params: { listId: list.id } })}
                >
                  <Td>
                    <div className="flex items-center gap-3">
                      <span
                        className="flex size-7 shrink-0 items-center justify-center rounded-md"
                        style={{
                          background: `${LIST_COLOR_HEX[list.color]}1f`,
                          color: LIST_COLOR_HEX[list.color],
                        }}
                      >
                        <ListChecksIcon className="size-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium">{list.name}</div>
                        {list.description && (
                          <div className="truncate text-[12px] text-fg-muted">{list.description}</div>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">{list.contactCount}</Td>
                  <Td className="text-right tabular-nums">{list.recipientCount}</Td>
                  <Td className="hidden text-fg-muted md:table-cell">
                    {list.editorIds.map(memberName).filter(Boolean).join(", ") || (
                      <span className="text-fg-subtle">-</span>
                    )}
                  </Td>
                  <Td className="hidden text-right text-[12.5px] text-fg-subtle sm:table-cell">
                    {formatRelative(list.updatedAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <ListFormDialog
        open={creating}
        onOpenChange={setCreating}
        initialKind={search.new}
        onSaved={(list) =>
          navigate({ to: "/lists/$listId", params: { listId: list.id }, search: { tab: "assignments" } })
        }
      />
    </Page>
  );
}
